import { computed, onMounted, ref } from 'vue';
import { defineContext, injectContext, provideContext } from './context.js';
import { reactive } from './reactivity/reactive.js';
/**
 * Internal context definition for the loading system.
 */
const LoadingContextSymbol = defineContext('LoadingContext');
let counter = 0;
/**
 * Creates and provides a loading context for child components to communicate their loading states.
 * This function injects any parent context and creates a new context that tracks loading operations
 * both from this component and its children separately.
 *
 * The context uses two reference counters:
 * - One for operations started in this context (loadingSelf)
 * - One for operations started in child contexts (loadingChild)
 *
 * @example
 * ```typescript
 * // In a component
 * const loading = useLoadingContext({ delay: 100 });
 *
 * // Start a loading operation
 * const stopLoading = loading.start();
 * // ... do async work ...
 * stopLoading();
 *
 * // Or use the run helper
 * await loading.run(async () => {
 *   // ... async work ...
 * });
 *
 * // Check loading states
 * console.log(loading.loadingSelf); // true if this context is loading
 * console.log(loading.loadingChild); // true if children are loading
 * console.log(loading.loading); // true if this or children are loading
 * console.log(loading.loadedOnce); // true if loaded at least once
 * ```
 *
 * @param opts Configuration options
 * @returns The loading context interface that this component and child components can access
 */
export function useLoadingContext(opts = {}) {
    const parent = injectContext(LoadingContextSymbol, { optional: true });
    const counterSelf = ref(0);
    const counterChild = ref(0);
    const loadedOnceRef = ref(false);
    const id = counter++;
    const incrementChild = () => {
        counterChild.value++;
        parent?.incrementChild();
    };
    const decrementChild = () => {
        counterChild.value--;
        checkLoadedOnce();
        parent?.decrementChild();
    };
    const start = () => {
        counterSelf.value++;
        parent?.incrementChild();
        let ended = false;
        return () => {
            if (ended) {
                return;
            }
            ended = true;
            setTimeout(() => {
                counterSelf.value--;
                parent?.decrementChild();
                checkLoadedOnce();
            }, opts.delay);
        };
    };
    const run = async (fn) => {
        const finalize = start();
        try {
            return await fn();
        }
        finally {
            finalize();
        }
    };
    const checkLoadedOnce = () => {
        if (counterSelf.value === 0 && counterChild.value === 0 && !loadedOnceRef.value) {
            loadedOnceRef.value = true;
        }
    };
    const context = reactive({
        get parent() {
            return parent ?? undefined;
        },
        get root() {
            return parent?.root ?? context;
        },
        loadingChild: computed(() => counterChild.value > 0),
        loadingSelf: computed(() => counterSelf.value > 0),
        loading: computed(() => counterSelf.value > 0 || counterChild.value > 0),
        loadedOnce: computed(() => loadedOnceRef.value),
        id,
        start,
        run,
        incrementChild,
        decrementChild,
    });
    provideContext(LoadingContextSymbol, context);
    onMounted(() => {
        setTimeout(checkLoadedOnce, 50);
    });
    return context;
}
//# sourceMappingURL=useLoadingContext.js.map